import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import {
	CONDITIONAL_FIELDS,
	RenderVariant,
	ToggleField as SharedToggleField,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../constants';
import { CardSize, CardsLayout } from '../../../../types';
import { CardsAIDisclaimer } from '../../../cards-extension/cards-content/ai-disclaimer';

import { messages } from './messages';

const {
	IS_AVATAR_SHOWN,
	IS_PUBLISH_DATE_SHOWN,
	IS_SOCIAL_STATS_SHOWN,
	IS_ICON_SHOWN,
	IS_LABEL_SHOWN,
	IS_DESCRIPTION_SHOWN,
} = CONDITIONAL_FIELDS;

type CardsConfigContentSectionDisplayOptionsProps = {
	isAvatarShown: boolean;
	isDescriptionShown?: boolean;
	isIconShown?: boolean;
	isLabelShown?: boolean;
	isPublishDateShown: boolean;
	isSocialStatsShown?: boolean;
	layout?: CardsLayout;
	renderVariant: RenderVariant;
	size?: CardSize;
	updateMainFormField: (fields: Record<string, boolean>) => void;
};

export const CardsConfigContentSectionDisplayOptions = ({
	renderVariant,
	isAvatarShown,
	isPublishDateShown,
	isSocialStatsShown,
	isIconShown,
	isLabelShown,
	isDescriptionShown,
	layout,
	size,
	updateMainFormField,
}: CardsConfigContentSectionDisplayOptionsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isAdminHubAIEnabled } = useProductDependencies();

	const handleAvatarToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_AVATAR_SHOWN]: !isAvatarShown,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'ownerToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: !isAvatarShown,
					renderVariant,
				},
			},
		}).fire();
	}, [updateMainFormField, isAvatarShown, renderVariant, createAnalyticsEvent]);

	const handlePublishDateToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_PUBLISH_DATE_SHOWN]: !isPublishDateShown,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'lastUpdateToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: !isPublishDateShown,
					renderVariant,
				},
			},
		}).fire();
	}, [updateMainFormField, isPublishDateShown, renderVariant, createAnalyticsEvent]);

	const handleSocialStatsToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_SOCIAL_STATS_SHOWN]: !isSocialStatsShown,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'socialStatsToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: !isSocialStatsShown,
					renderVariant,
				},
			},
		}).fire();
	}, [updateMainFormField, isSocialStatsShown, renderVariant, createAnalyticsEvent]);

	const handleIconToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_ICON_SHOWN]: !isIconShown,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'iconToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: !isIconShown,
					renderVariant,
				},
			},
		}).fire();
	}, [updateMainFormField, isIconShown, renderVariant, createAnalyticsEvent]);

	const handleLabelToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_LABEL_SHOWN]: !isLabelShown,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'labelToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: !isLabelShown,
					renderVariant,
				},
			},
		}).fire();
	}, [updateMainFormField, isLabelShown, renderVariant, createAnalyticsEvent]);

	const handleDescriptionToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_DESCRIPTION_SHOWN]: !isDescriptionShown,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'descriptionToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: !isDescriptionShown,
					renderVariant,
				},
			},
		}).fire();
	}, [updateMainFormField, isDescriptionShown, renderVariant, createAnalyticsEvent]);

	return (
		<Stack space="space.075">
			<SharedToggleField
				label={formatMessage(messages.avatarToggleLabel)}
				isChecked={isAvatarShown}
				onChange={handleAvatarToggleChange}
			/>
			<SharedToggleField
				label={formatMessage(messages.publishDateToggleLabel)}
				isChecked={isPublishDateShown}
				onChange={handlePublishDateToggleChange}
			/>
			{size !== CardSize.SMALL && fg('company_hub_cards_visual_update') && (
				<>
					<SharedToggleField
						label={formatMessage(messages.socialStatsToggleLabel)}
						isChecked={isSocialStatsShown}
						onChange={handleSocialStatsToggleChange}
					/>
					<SharedToggleField
						label={formatMessage(messages.iconToggleLabel)}
						isChecked={isIconShown}
						onChange={handleIconToggleChange}
					/>
				</>
			)}
			{layout === CardsLayout.FIXED_IMAGE_HEIGHT &&
				renderVariant === RenderVariant.MANUAL &&
				size !== CardSize.SMALL &&
				fg('company_hub_cards_v2') && (
					<SharedToggleField
						label={formatMessage(messages.labelToggleLabel)}
						isChecked={isLabelShown}
						onChange={handleLabelToggleChange}
					/>
				)}
			{renderVariant === RenderVariant.DYNAMIC &&
				size !== CardSize.SMALL &&
				fg('company_hub_dynamic_cards_ai_descriptions') && (
					<>
						<SharedToggleField
							label={formatMessage(messages.descriptionToggleLabel)}
							isChecked={isDescriptionShown}
							onChange={handleDescriptionToggleChange}
						/>
						{isAdminHubAIEnabled && <CardsAIDisclaimer />}
					</>
				)}
		</Stack>
	);
};
